import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Loader2, AlertTriangle } from 'lucide-react'
import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

/**
 * SsoLanding — where someone arrives from another product's "open in ProTrack" button.
 *
 * They land here holding a 60-second token their own product signed. We exchange it for a
 * ProTrack session and send them straight to that product's screens. They never see a login
 * form, because they already proved who they are on the other side.
 *
 * The token travels in the URL FRAGMENT, not a query string. A fragment is never sent to any
 * server, so it stays out of our access logs, their redirect logs and any Referer header — which
 * matters when the thing in the URL is, for its minute of life, a password. We also strip it from
 * the address bar the moment it is read, so it does not linger in history or on a shared screen.
 */
export default function SsoLanding() {
  const { productKey } = useParams()
  const [error, setError] = useState(null)
  const started = useRef(false)   // the token is single-use; React must not spend it twice

  useEffect(() => {
    if (started.current) return
    started.current = true

    const hash = new URLSearchParams((window.location.hash || '').replace(/^#/, ''))
    const token = hash.get('t')
    // Read once, then remove — no reload, so this does not re-trigger the exchange.
    window.history.replaceState(null, '', window.location.pathname)

    if (!token) {
      setError('This sign-in link is missing its token. Please click the button in your product again.')
      return
    }

    axios.post(`${API_URL}/platform/sso/${productKey}`, { token })
      .then(({ data }) => {
        // Same shape the normal login stores, so everything downstream behaves identically.
        localStorage.setItem('user', JSON.stringify({
          access_token: data.access_token,
          token_type: data.token_type,
          ...(data.user || {}),
        }))
        // A full navigation rather than a route change: the auth provider reads localStorage
        // once on mount, so a soft transition would leave it holding the old session.
        window.location.replace(`/products/${productKey}`)
      })
      .catch(e => {
        const d = e.response?.data?.detail
        // Someone who works in both places. Their token may not open a ProTrack session — that
        // rule is what stops the product's secret becoming a login for our own people — but
        // stranding them on an error is a poor way to say so. Their access has just been
        // recorded, so send them to sign in and then straight on to where they were heading:
        // one password, and they arrive where the button promised.
        if (e.response?.status === 403 && typeof d === 'string' && d.includes('already have a ProTrack account')) {
          try { sessionStorage.setItem('postLoginRedirect', `/products/${productKey}/rooms`) } catch { /* ignore */ }
          setError(d + ' Taking you to sign in…')
          setTimeout(() => window.location.replace('/login'), 2200)
          return
        }
        setError(typeof d === 'string' ? d : 'That sign-in link could not be used. Please try again from your product.')
      })
  }, [productKey])

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </p>
          <p className="text-xs text-gray-500 mt-3">
            Sign-in links are valid for one minute and can only be used once, so an old link or a
            second click will always land here.
          </p>
          <a href="/login" className="mt-4 inline-block text-xs font-semibold text-indigo-700 hover:underline">
            Sign in to ProTrack instead
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <p className="text-sm text-gray-500 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Signing you in…
      </p>
    </div>
  )
}
